// src/pages/OfflineOrdersPage.js
import React from 'react';
import { CloudOff, RefreshCw, Trash2 } from 'lucide-react';
import OfflineOrdersList from '../components/offline/OfflineOrdersList';
import { useOfflineStorage } from '../hooks/useOfflineStorage';

const OfflineOrdersPage = () => {
  const { 
    isOnline, 
    offlineOrders, 
    pendingSyncCount, 
    syncPendingOrders, 
    clearSyncedOrders 
  } = useOfflineStorage();

  const handleSyncAll = async () => {
    if (!isOnline) {
      alert('Connessione non disponibile');
      return;
    }

    try {
      const result = await syncPendingOrders();
      
      if (result.success) {
        if (result.syncedCount === result.totalCount) {
          alert(`✅ Sincronizzazione completata con successo!\n${result.syncedCount} ordini sincronizzati.`);
        } else {
          alert(`⚠️ Sincronizzazione parziale:\n${result.syncedCount}/${result.totalCount} ordini sincronizzati.\n\nErrori: ${result.errors.length}`);
        }
      } else {
        alert(`❌ Sincronizzazione fallita:\nSolo ${result.syncedCount}/${result.totalCount} ordini sincronizzati.\n\nControlla la console per dettagli.`);
      }
    } catch (error) {
      console.error('💥 Errore handleSyncAll:', error);
      alert(`❌ Errore durante la sincronizzazione:\n${error.message}`);
    }
  };

  const handleClearSynced = () => {
    if (window.confirm('Rimuovere tutti gli ordini già sincronizzati?')) {
      clearSyncedOrders();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CloudOff className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Gestione Ordini Offline
                </h1>
                <p className="text-gray-600">
                  Monitora e sincronizza gli ordini creati in modalità offline
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {pendingSyncCount > 0 && isOnline && (
                <button
                  onClick={handleSyncAll}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Sincronizza Tutti</span>
                </button>
              )}
              
              <button
                onClick={handleClearSynced}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <Trash2 className="w-4 h-4" />
                <span>Pulisci Sincronizzati</span>
              </button>
            </div>
          </div>

          {/* Statistiche */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Totale Ordini</p>
                  <p className="text-2xl font-bold text-blue-900">{offlineOrders.length}</p>
                </div>
                <CloudOff className="w-8 h-8 text-blue-500" />
              </div>
            </div>
            
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium">Da Sincronizzare</p>
                  <p className="text-2xl font-bold text-orange-900">{pendingSyncCount}</p>
                </div>
                <RefreshCw className="w-8 h-8 text-orange-500" />
              </div>
            </div>
            
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Sincronizzati</p>
                  <p className="text-2xl font-bold text-green-900">
                    {offlineOrders.filter(o => o.synced).length}
                  </p>
                </div>
                <RefreshCw className="w-8 h-8 text-green-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Lista ordini offline */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          {offlineOrders.length === 0 ? (
            <div className="text-center py-12">
              <CloudOff className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nessun ordine offline
              </h3>
              <p className="text-gray-600">
                Gli ordini creati in modalità offline appariranno qui
              </p>
            </div>
          ) : (
            <OfflineOrdersList />
          )}
        </div>

        {/* Guida modalità offline */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            Come funziona la modalità offline
          </h3>
          <div className="space-y-2 text-sm text-blue-800">
            <p>• <strong>Creazione ordini:</strong> Puoi creare ordini anche senza connessione</p>
            <p>• <strong>Dati cachati:</strong> Clienti e prodotti vengono salvati per uso offline</p>
            <p>• <strong>Sincronizzazione:</strong> Al ritorno della connessione, gli ordini vengono inviati automaticamente</p>
            <p>• <strong>Backup locale:</strong> Tutti i dati sono conservati sul dispositivo fino alla sincronizzazione</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfflineOrdersPage;